import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as microsoftTeams from '@microsoft/teams-js';
import { 
  FluentProvider, 
  webLightTheme,
  Text,
  Button,
  Card
} from '@fluentui/react-components';
import { Checkmark24Regular } from '@fluentui/react-icons';

/**
 * TabConfig component
 * This component is shown when a user adds the app as a tab to a Teams channel.
 * It handles the configuration flow required by Microsoft Teams.
 */
export const TabConfig: React.FC = () => {
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Initialize Teams SDK
        await microsoftTeams.app.initialize();
        setInitialized(true);

        // Get the Teams context to access channel information
        const context = await microsoftTeams.app.getContext();
        
        // Configure the settings for the configuration page
        microsoftTeams.pages.config.registerOnSaveHandler((saveEvent) => {
          // Content URL with placeholders: Teams replaces these when the tab loads.
          // Important for Mobile: getContext() often returns empty values there.
          //
          // Officially supported placeholders (replaced on both Desktop AND Mobile):
          //   {groupId}   – Team/Group ID (works reliably everywhere)
          //   {channelId} – Channel ID (officially supported, works on Mobile)
          //   {tid}       – Tenant ID
          //   {loginHint} – User Principal Name
          //
          // Custom/unofficial placeholders ({channelName}, {teamName}):
          //   These may NOT be resolved on Mobile Teams – they remain as literal strings.
          //   The app detects this and falls back gracefully.
          const base = `${window.location.origin}/index.html`;
          const contentUrl = `${base}?groupId={groupId}&channelId={channelId}&channelName={channelName}&teamName={teamName}`;
          const websiteUrl = base;
          
          microsoftTeams.pages.config.setConfig({
            contentUrl: contentUrl,
            websiteUrl: websiteUrl,
            suggestedDisplayName: t('tabConfig.suggestedDisplayName'),
            entityId: `drawio-${context.channel?.id || context.chat?.id || 'default'}`,
          });

          saveEvent.notifySuccess();
        });

        // Enable the Save button
        microsoftTeams.pages.config.setValidityState(true);

      } catch (err) {
        console.error('Failed to initialize Teams SDK:', err);
        setError(err instanceof Error ? err.message : t('tabConfig.errorInit'));
      }
    };

    initializeTeams();
  }, [t]);

  if (error) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Text size={500} weight="semibold" style={{ color: 'red' }}>
            {t('tabConfig.errorInit')}
          </Text>
          <Text size={300} style={{ marginTop: '10px', display: 'block' }}>
            {error}
          </Text>
        </div>
      </FluentProvider>
    );
  }

  if (!initialized) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Text size={400}>{t('tabConfig.loading')}</Text>
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <div style={{ 
        padding: '40px 20px', 
        maxWidth: '600px', 
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <Card style={{ padding: '30px' }}>
          <div style={{ marginBottom: '20px' }}>
            <Checkmark24Regular 
              style={{ 
                color: '#107C10', 
                fontSize: '48px',
                marginBottom: '10px'
              }} 
            />
          </div>
          
          <Text 
            size={600} 
            weight="semibold" 
            style={{ display: 'block', marginBottom: '15px' }}
          >
            {t('tabConfig.title')}
          </Text>
          
          <Text 
            size={400} 
            style={{ display: 'block', marginBottom: '20px', color: '#605E5C' }}
          >
            {t('tabConfig.description')}
          </Text>

          <div style={{ 
            background: '#F3F2F1', 
            padding: '15px', 
            borderRadius: '4px',
            marginTop: '20px'
          }}>
            <Text size={300} style={{ display: 'block', textAlign: 'left' }}>
              <strong>{t('tabConfig.featuresTitle')}</strong>
              <ul style={{ marginTop: '10px', textAlign: 'left', paddingLeft: '20px' }}>
                <li>{t('tabConfig.feature1')}</li>
                <li>{t('tabConfig.feature2')}</li>
                <li>{t('tabConfig.feature3')}</li>
              </ul>
            </Text>
          </div>

          <div style={{ marginTop: '30px' }}>
            <Text size={300} style={{ color: '#605E5C' }}>
              {t('tabConfig.saveHint')}
            </Text>
          </div>
        </Card>
      </div>
    </FluentProvider>
  );
};

export default TabConfig;
